<script lang="ts">
	import type { EmblaCarouselType } from "embla-carousel";
	import WheelGesturesPlugin from "embla-carousel-wheel-gestures";
	import { onDestroy } from "svelte";
	import Carousel from "./Carousel.svelte";
	import Link from "./Link.svelte";
	import { getTenant } from "../getTenant.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./HomepageProjectApproach.translations.json";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap.js";

	const translate = getTranslate(translations);
	const tenant = getTenant();
	// eslint-disable-next-line new-cap
	const wheelPlugin = WheelGesturesPlugin();

	let api: EmblaCarouselType | undefined = undefined;
	let slidesInView: number[] = [];
	const permanentlyActiveSlides = new Set<number>([0, 1, 2]);
	let scrollProgress = 0;
	let visibleFraction = 1;
	let canNext = false;
	let canPrev = false;

	interface Step {
		number: string;
		title: string;
		subtitle: string;
		text: string;
	}

	const steps: Step[] = [
		{
			number: "01",
			title: translate("step1Title"),
			subtitle: translate("step1Subtitle"),
			text: translate("step1Text"),
		},
		{
			number: "02",
			title: translate("step2Title"),
			subtitle: translate("step2Subtitle"),
			text: translate("step2Text"),
		},
		{
			number: "03",
			title: translate("step3Title"),
			subtitle: translate("step3Subtitle"),
			text: translate("step3Text"),
		},
		{
			number: "04",
			title: translate("step4Title"),
			subtitle: translate("step4Subtitle"),
			text: translate("step4Text"),
		},
		{
			number: "05",
			title: translate("step5Title"),
			subtitle: translate("step5Subtitle"),
			text: translate("step5Text"),
		},
	];

	let cleanup: (() => void) | undefined;

	$: {
		cleanup?.();
		cleanup = undefined;

		const apiInstance = api;

		if (apiInstance) {
			const onUpdate = (): void => {
				scrollProgress = Math.max(0, Math.min(1, apiInstance.scrollProgress()));
				slidesInView = apiInstance.slidesInView();
				const viewportWidth = apiInstance.containerNode().clientWidth;
				const contentWidth = apiInstance
					.slideNodes()
					.reduce((sum, node) => sum + node.getBoundingClientRect().width, 0);
				visibleFraction = contentWidth > 0 ? Math.min(1, viewportWidth / contentWidth) : 1;
			};

			onUpdate();
			apiInstance.on("scroll", onUpdate);
			apiInstance.on("select", onUpdate);
			apiInstance.on("reInit", onUpdate);
			apiInstance.on("resize", onUpdate);

			cleanup = (): void => {
				apiInstance.off("scroll", onUpdate);
				apiInstance.off("select", onUpdate);
				apiInstance.off("reInit", onUpdate);
				apiInstance.off("resize", onUpdate);
			};
		} else {
			scrollProgress = 0;
			slidesInView = [];
			visibleFraction = 1;
		}
	}

	onDestroy(() => {
		cleanup?.();
	});

	function scrollTo(index: number): void {
		api?.scrollTo(index);
	}

	function scrollToHandler(index: number): () => void {
		return () => {
			scrollTo(index);
		};
	}

	function asStep(item: unknown): Step {
		return item as Step;
	}

	function asIndex(value: unknown): number {
		return value as number;
	}
</script>

<!-- Intro section -->
<div class="text-ltprimary mx-4 mb-24 md:mx-0">
	<div class="flex items-center gap-4 md:mx-16 xl:mx-32">
		<span class="bg-ltprimary-200 h-px w-8"></span>
		<span class="text-ltprimary-200 uppercase">{translate("tag")}</span>
	</div>
	<div class="mt-16 flex flex-col gap-2 md:mx-16 md:gap-8 xl:mx-32 xl:flex-row xl:gap-32">
		<div class="flex-1 xl:max-w-[40%]">
			<h2
				class="text-ltprimary text-2lg p-0 text-center font-light uppercase leading-[1.3] tracking-wider md:text-left lg:text-xl xl:text-2xl"
			>
				{translate("introductoryHeadline")}
			</h2>
		</div>
		<div class="text-ltsecondary max-w-3xl flex-1">
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">{translate("desc")}</div>
			<Link
				asButton
				variant="secondary"
				class="mt-12 xl:mt-16"
				icon={arrowRight}
				href="/{contactPathMap[tenant.locale]}">{translate("contactUs")}</Link
			>
		</div>
	</div>
</div>

<Carousel
	bind:api
	bind:canNext
	bind:canPrev
	items={steps}
	options={{
		align: "start",
		containScroll: "trimSnaps",
		dragFree: true,
		dragThreshold: 0,
		watchDrag: true,
	}}
	plugins={[wheelPlugin]}
	itemsContainerClass="gap-0 mx-4 md:mx-16 xl:mx-32"
>
	<div
		slot="default"
		let:item
		let:index
		class="!min-w-80 basis-[85%] sm:basis-1/2 md:!min-w-[32rem] lg:basis-1/3 xl:basis-1/4"
	>
		{@const step = asStep(item)}
		{@const i = asIndex(index)}

		<div
			class="border-lttertiary -mx-1 flex h-full flex-col border-b border-l {i === steps.length - 1 ? 'border-r' : ''}"
		>
			<!-- Stepper -->
			<div class="relative h-36 items-start p-8 md:flex lg:h-48 xl:h-64">
				<div class="bg-lttertiary absolute inset-0"></div>
				<button
					class="font-bahnschrift relative z-10 flex size-16 shrink-0 items-center justify-center rounded-full border text-[2.5rem] tabular-nums transition-all duration-300 md:size-20
						{permanentlyActiveSlides.has(i) || slidesInView.includes(i)
						? 'border-white text-white'
						: 'border-white/40 text-white/40'}"
					on:click={scrollToHandler(i)}
				>
					{step.number}
				</button>
				<!-- Connecting border -->
				<div class="relative -mr-12 -mt-7 ml-24 h-0.5 flex-1 bg-white/20 md:ml-7 md:mt-10 xl:ml-12">
					<div
						class="absolute left-0 top-0 h-full bg-white/60 transition-all duration-500"
						style="width: {permanentlyActiveSlides.has(i) || slidesInView.includes(i) ? '100%' : '0%'}"
					></div>
				</div>
			</div>

			<!-- Content -->
			<div class="border-lttertiary flex flex-1 flex-col border-t p-8 md:border-t-0">
				<h3 class="text-ltprimary text-2md mb-4 font-normal uppercase tracking-[0.02em]">
					{step.title}
				</h3>
				<p class="text-ltsecondary mb-4 text-base/[1.6] font-bold uppercase">
					{step.subtitle}
				</p>

				<p class="text-ltsecondary text-base/[1.6] font-normal">
					{step.text}
				</p>
			</div>
		</div>
	</div>
</Carousel>

<!-- Progress bar -->
<div class="mx-4 mt-12 flex h-1 items-center gap-1 md:mx-auto md:mt-16 md:max-w-lg">
	<div class="relative h-full flex-1 overflow-hidden rounded-full bg-gray-200">
		<div
			class="bg-ltprimary-300 absolute inset-y-0 left-0 rounded-full transition-[left,width] duration-150 ease-out"
			style="width: {visibleFraction * 100}%; left: {scrollProgress * (1 - visibleFraction) * 100}%;"
		></div>
	</div>
</div>
